import { useEffect, useState } from "react";
import api, { fmtIDR, fmtDate } from "@/lib/api";
import { PageHeader, Loading, Empty } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, ScanLine, PackagePlus } from "lucide-react";
import BarcodeScanner from "@/components/BarcodeScanner";

export default function Purchases() {
  const [rows, setRows] = useState([]);
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [supplier, setSupplier] = useState("umum");
  const [paid, setPaid] = useState(0);
  const [items, setItems] = useState([]);
  const [scanOpen, setScanOpen] = useState(false);
  const [newItem, setNewItem] = useState({ product_name: "", capacity: "", imei: "", qty: 1, cost_price: 0 });

  const cancel = async (r) => {
    if (!window.confirm(`Batalkan pembelian ${r.invoice}? Stok akan dikurangi kembali.`)) return;
    try { await api.put(`/purchases/${r.id}/cancel`); toast.success("Pembelian dibatalkan"); load(); api.get("/products").then(({ data }) => setProducts(data)); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const load = () => { setLoading(true); api.get("/purchases").then(({ data }) => setRows(data)).finally(() => setLoading(false)); };
  useEffect(() => { load(); api.get("/products").then(({ data }) => setProducts(data)); api.get("/suppliers").then(({ data }) => setSuppliers(data)); }, []);

  const addExisting = (pid) => {
    const p = products.find((x) => x.id === pid); if (!p) return;
    if (items.find((i) => i.product_id === pid)) return;
    setItems([...items, { product_id: p.id, product_name: p.name, capacity: "", imei: p.imei || "", qty: 1, cost_price: p.cost_price }]);
  };
  const addNew = () => {
    if (!newItem.product_name) return toast.error("Nama barang wajib");
    setItems([...items, { product_id: null, ...newItem, qty: Number(newItem.qty || 1), cost_price: Number(newItem.cost_price || 0) }]);
    setNewItem({ product_name: "", capacity: "", imei: "", qty: 1, cost_price: 0 });
  };
  const upd = (idx, k, v) => setItems(items.map((i, x) => x === idx ? { ...i, [k]: (k === "qty" || k === "cost_price") ? Number(v) : v } : i));
  const total = items.reduce((s, i) => s + i.qty * i.cost_price, 0);

  const scanAdd = async (code) => {
    setScanOpen(false);
    try {
      const { data } = await api.get("/products", { params: { q: code } });
      if (data && data.length) { addExisting(data[0].id); toast.success(`Produk ditemukan: ${data[0].name}`); }
      else { setNewItem((n) => ({ ...n, imei: code })); toast.info("Produk baru — lengkapi data lalu Tambah"); }
    } catch { toast.error("Gagal mencari produk"); }
  };

  const save = async () => {
    if (items.length === 0) return toast.error("Tambah minimal 1 item");
    const sup = suppliers.find((s) => s.id === supplier);
    try {
      await api.post("/purchases", {
        supplier_id: supplier === "umum" ? null : supplier, supplier_name: sup?.name || "Umum",
        items, paid: Number(paid || 0), payment_method: "cash",
      });
      toast.success("Barang masuk & stok diperbarui otomatis"); setOpen(false); setItems([]); setPaid(0); load();
      api.get("/products").then(({ data }) => setProducts(data));
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan penerimaan barang"); }
  };

  return (
    <div>
      <PageHeader title="Penerimaan Barang Supplier" subtitle="Catat barang masuk — otomatis menambah stok & database produk">
        <Button onClick={() => setOpen(true)} className="bg-sky-600 hover:bg-sky-700" data-testid="purchase-add-btn"><Plus className="w-4 h-4 mr-1.5" /> Barang Masuk</Button>
      </PageHeader>
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200"><tr>
            {["Invoice", "Supplier", "Item", "Total", "Terbayar", "Sisa", "Tanggal", "Aksi"].map((h) => <th key={h} className="px-4 py-3 text-left font-medium text-slate-500">{h}</th>)}
          </tr></thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? <tr><td colSpan={8}><Loading /></td></tr>
              : rows.length === 0 ? <tr><td colSpan={8}><Empty /></td></tr>
              : rows.map((r) => (
                <tr key={r.id} className={`hover:bg-slate-50 ${r.status === "dibatalkan" ? "opacity-50" : ""}`}>
                  <td className="px-4 py-3 font-mono-num text-xs">{r.invoice}</td>
                  <td className="px-4 py-3">{r.supplier_name}</td>
                  <td className="px-4 py-3">{r.items?.length || 0} item</td>
                  <td className="px-4 py-3 font-mono-num">{fmtIDR(r.total)}</td>
                  <td className="px-4 py-3 font-mono-num text-green-600">{fmtIDR(r.paid)}</td>
                  <td className="px-4 py-3 font-mono-num text-red-600">{fmtIDR(r.due)}</td>
                  <td className="px-4 py-3 text-slate-500">{fmtDate(r.date)}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {r.status === "dibatalkan" ? <span className="text-xs text-red-500">Dibatalkan</span>
                      : <Button size="sm" variant="outline" className="text-xs text-red-600 border-red-200" onClick={() => cancel(r)} data-testid={`purchase-cancel-${r.id}`}>Batalkan</Button>}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Penerimaan Barang dari Supplier</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-slate-500">Supplier</Label>
                <Select value={supplier} onValueChange={setSupplier}>
                  <SelectTrigger className="mt-1" data-testid="purchase-supplier"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="umum">Umum</SelectItem>{suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs text-slate-500">Tambah Produk Lama (opsional)</Label>
                <div className="mt-1 flex gap-2">
                  <div className="flex-1">
                    <Select onValueChange={addExisting}>
                      <SelectTrigger data-testid="purchase-add-item"><SelectValue placeholder="Pilih produk" /></SelectTrigger>
                      <SelectContent>{products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <Button type="button" variant="outline" className="shrink-0" onClick={() => setScanOpen(true)} data-testid="purchase-scan-btn"><ScanLine className="w-4 h-4" /></Button>
                </div>
              </div>
            </div>

            {/* Input barang BARU */}
            <div className="bg-sky-50/70 border border-sky-100 rounded-lg p-3">
              <Label className="text-xs text-slate-600 font-medium flex items-center gap-1.5"><PackagePlus className="w-4 h-4 text-sky-600" /> Barang Baru (otomatis masuk database produk)</Label>
              <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mt-2 items-end">
                <div className="col-span-2"><Label className="text-[11px] text-slate-500">Nama Barang</Label><Input value={newItem.product_name} onChange={(e) => setNewItem({ ...newItem, product_name: e.target.value })} className="mt-0.5 bg-white h-9" data-testid="new-item-name" /></div>
                <div><Label className="text-[11px] text-slate-500">Kapasitas</Label><Input value={newItem.capacity} onChange={(e) => setNewItem({ ...newItem, capacity: e.target.value })} placeholder="128GB" className="mt-0.5 bg-white h-9" data-testid="new-item-capacity" /></div>
                <div><Label className="text-[11px] text-slate-500">IMEI</Label><Input value={newItem.imei} onChange={(e) => setNewItem({ ...newItem, imei: e.target.value })} className="mt-0.5 bg-white h-9" data-testid="new-item-imei" /></div>
                <div><Label className="text-[11px] text-slate-500">Qty</Label><Input type="number" value={newItem.qty} onChange={(e) => setNewItem({ ...newItem, qty: e.target.value })} className="mt-0.5 bg-white h-9" data-testid="new-item-qty" /></div>
                <div><Label className="text-[11px] text-slate-500">Modal</Label><Input type="number" value={newItem.cost_price} onChange={(e) => setNewItem({ ...newItem, cost_price: e.target.value })} className="mt-0.5 bg-white h-9" data-testid="new-item-cost" /></div>
              </div>
              <Button type="button" size="sm" variant="outline" className="mt-2" onClick={addNew} data-testid="new-item-add"><Plus className="w-3.5 h-3.5 mr-1" /> Tambah ke Daftar</Button>
            </div>

            <div className="space-y-2 max-h-56 overflow-y-auto">
              {items.length === 0 && <p className="text-xs text-slate-400 text-center py-2">Belum ada barang. Tambahkan produk lama atau barang baru di atas.</p>}
              {items.map((i, idx) => (
                <div key={idx} className="flex items-center gap-2 text-sm border-b border-slate-100 pb-2">
                  <div className="flex-1 truncate">{i.product_name}{i.capacity ? ` · ${i.capacity}` : ""}{i.imei ? ` · IMEI ${i.imei}` : ""}{!i.product_id && <span className="ml-1 text-[10px] bg-sky-100 text-sky-700 px-1.5 py-0.5 rounded">BARU</span>}</div>
                  <Input type="number" value={i.qty} onChange={(e) => upd(idx, "qty", e.target.value)} className="w-16 h-8" placeholder="Qty" />
                  <Input type="number" value={i.cost_price} onChange={(e) => upd(idx, "cost_price", e.target.value)} className="w-28 h-8" placeholder="Modal" />
                  <button onClick={() => setItems(items.filter((_, x) => x !== idx))} className="p-1 text-red-500"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
            <div className="flex justify-between items-center pt-2 border-t border-slate-200">
              <div className="font-mono-num text-lg font-semibold">Total: {fmtIDR(total)}</div>
              <div className="flex items-center gap-2"><Label className="text-xs text-slate-500">Bayar</Label><Input type="number" value={paid} onChange={(e) => setPaid(e.target.value)} className="w-32 h-9" /></div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Batal</Button><Button onClick={save} className="bg-sky-600 hover:bg-sky-700" data-testid="purchase-save-btn">Simpan & Update Stok</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <BarcodeScanner open={scanOpen} onClose={() => setScanOpen(false)} onScan={scanAdd} />
    </div>
  );
}
